import type {
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from "lexical";

import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from "@lexical/react/LexicalDecoratorBlockNode";
import * as React from "react";
import { DOMExportOutput } from "lexical";
import { PATH_SCOPE } from "main/javascripts/constants/Constants";

type LineLinkComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  lineLinkClassName?: string;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  url: string;
  buttonText: string;
  promotionText?: string;
}>;

export interface LineLinkPayload {
  url: string;
  buttonText: string;
  promotionText?: string;
}

// importで画像を読み込むと、nest-imagesの設定の関係でbase64化されてしまうため
// publicディレクトリから読み込む
const iconLinePath = `${PATH_SCOPE}/main/images/icons/line.svg`;

function LineLinkComponent({
  className,
  lineLinkClassName,
  format,
  nodeKey,
  url,
  buttonText,
  promotionText,
}: LineLinkComponentProps) {
  return (
    <BlockWithAlignableContents
      className={className}
      format={format}
      nodeKey={nodeKey}
    >
      <div className={lineLinkClassName}>
        {promotionText && (
          <div className="promotion">
            <div className="promotion-text">{promotionText}</div>
          </div>
        )}
        <a
          className="button line-add-friend"
          href={url}
          target="_blank"
          data-event-category="LINE Add"
          data-event-label="Article Button01"
          rel="noopener noreferrer"
        >
          <div className="inner">
            <div className="symbol">
              <img src={iconLinePath} alt="LINE" />
            </div>
            <div className="text">
              <span>{buttonText}</span>
            </div>
          </div>
        </a>
      </div>
    </BlockWithAlignableContents>
  );
}

export type SerializedLineLinkNode = Spread<
  {
    url: string;
    buttonText: string;
    promotionText?: string;
  },
  SerializedDecoratorBlockNode
>;

export class LineLinkNode extends DecoratorBlockNode {
  __url: string;
  __buttonText: string;
  __promotionText: null | string;

  static getType(): string {
    return "lineLink";
  }

  static clone(node: LineLinkNode): LineLinkNode {
    return new LineLinkNode(
      node.__url,
      node.__buttonText,
      node.__promotionText,
      node.__format,
      node.__key
    );
  }

  static importJSON(serializedNode: SerializedLineLinkNode): LineLinkNode {
    const { url, buttonText, promotionText } = serializedNode;
    const node = $createLineLinkNode({ url, buttonText, promotionText });
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedLineLinkNode {
    return {
      ...super.exportJSON(),
      type: "lineLink",
      version: 1,
      url: this.__url,
      buttonText: this.__buttonText,
      promotionText: this.__promotionText,
    };
  }

  exportDOM(editor: LexicalEditor): DOMExportOutput {
    const element = document.createElement("div");
    const link = document.createElement("a");
    const linkInner = document.createElement("div");
    const symbolBlock = document.createElement("div");
    const symbolImage = document.createElement("img");
    const textBlock = document.createElement("div");
    const text = document.createElement("span");
    const promotionBlock = document.createElement("div");
    const promotionText = document.createElement("div");
    const theme = editor._config.theme;
    const className = theme.lineLink;
    if (className !== undefined) {
      element.className = className;
    }

    link.href = this.__url;
    link.target = "_blank";
    link.className = "button line-add-friend";
    linkInner.className = "inner";

    symbolImage.src = iconLinePath;
    symbolImage.alt = "LINE";
    symbolBlock.className = "symbol";
    symbolBlock.appendChild(symbolImage);

    if (this.__promotionText) {
      promotionBlock.className = "promotion";
      promotionText.className = "promotion-text";
      promotionText.textContent = this.__promotionText;
      promotionBlock.appendChild(promotionText);
    }

    textBlock.className = "text";
    text.textContent = this.__buttonText;
    textBlock.appendChild(text);

    linkInner.appendChild(symbolBlock);
    linkInner.appendChild(textBlock);
    link.appendChild(linkInner);

    element.appendChild(promotionBlock);
    element.appendChild(link);
    return { element };
  }

  constructor(
    url: string,
    buttonText: string,
    promotionText?: string,
    format?: ElementFormatType,
    key?: NodeKey
  ) {
    super(format, key);
    this.__url = url;
    this.__buttonText = buttonText;
    this.__promotionText = promotionText || null;
  }

  updateDOM(): false {
    return false;
  }

  getTextContent(
    _includeInert?: boolean | undefined,
    _includeDirectionless?: false | undefined
  ): string {
    let text = this.__promotionText ? `${this.__promotionText}\n` : "";
    text = `${text}[LINE]${this.__buttonText}\n${this.__url}`;
    return text;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || "",
      focus: embedBlockTheme.focus || "",
    };
    const lineLinkClassName = config.theme.lineLink;
    return (
      <LineLinkComponent
        className={className}
        lineLinkClassName={lineLinkClassName}
        format={this.__format}
        nodeKey={this.getKey()}
        url={this.__url}
        buttonText={this.__buttonText}
        promotionText={this.__promotionText}
      />
    );
  }
}

export function $createLineLinkNode({
  url,
  buttonText,
  promotionText,
}: LineLinkPayload): LineLinkNode {
  return new LineLinkNode(url, buttonText, promotionText);
}

export function $isLineLinkNode(
  node: LineLinkNode | LexicalNode | null | undefined
): node is LineLinkNode {
  return node instanceof LineLinkNode;
}
